import {queryCollection} from '@/services/FirestoreService';
import {PORTFOLIOS_COLLECTION, PROPERTIES_COLLECTION} from '../competitive/constants';

interface LinkedDocument {
  collection: string;
  id: string;
}

interface CompetitivePortfolioDoc {
  id: string;
  name?: string;
  generatedByOm?: boolean;
  underwritingData?: Record<string, unknown>;
  linkedDocuments?: LinkedDocument[];
  deleted?: Date | null;
}

function isValidDashboardPortfolio(doc: CompetitivePortfolioDoc): boolean {
  if (doc.generatedByOm !== true) return false;
  if (!doc.underwritingData) return false;
  if (Object.keys(doc.underwritingData).length === 0) return false;
  if (doc.deleted != null) return false;
  return true;
}

function linksToBuilding(doc: CompetitivePortfolioDoc, buildingId: string): boolean {
  const links = doc.linkedDocuments ?? [];
  return links.some((link) => link.collection === PROPERTIES_COLLECTION && link.id === buildingId);
}

/**
 * Returns the id of the unique valid "dashboard portfolio" that links this building,
 * or null if the building is standalone.
 *
 * A valid dashboard portfolio is one that is OM-generated, has non-empty
 * underwritingData, and lists the building in its linkedDocuments. By product
 * rule a building belongs to at most one such portfolio. If the data violates
 * that rule, we emit a console warning and return the first match so the caller
 * at least has a coherent result.
 */
export async function findValidParentPortfolioId(buildingId: string): Promise<string | null> {
  const candidates = (await queryCollection(
    PORTFOLIOS_COLLECTION,
    {where: [{field: 'generatedByOm', opStr: '==', value: true}]},
    true,
  )) as CompetitivePortfolioDoc[];

  const matches = candidates.filter(
    (doc) => isValidDashboardPortfolio(doc) && linksToBuilding(doc, buildingId),
  );

  if (matches.length === 0) return null;
  if (matches.length > 1) {
    console.warn(
      `Building ${buildingId} is linked to ${matches.length} valid dashboard portfolios: `
      + `${matches.map((m) => m.id).join(', ')}. Using the first one.`,
    );
  }
  return matches[0].id;
}
